import type {
  CellDataValidation,
  FormCell,
} from './form-config.types';
import type { CellValue } from './report-config.types';
import type { CellStyle } from './report-style.types';

/**
 * Celda de un bloque, posicionada en filas RELATIVAS (0-based) al
 * inicio del bloque. La columna sigue siendo absoluta ('B').
 */
export interface FormBlockCell {
  readonly col: string;
  readonly row: number;
  readonly value?: CellValue;
  /** Fórmula Excel SIN el `=` (ej. 'TODAY()'); ver `FormCell`. */
  readonly formula?: string;
  /** Desplegable de validación; ver `FormCell`. */
  readonly dataValidation?: CellDataValidation;
  readonly numFmt?: string;
  readonly style?: Partial<CellStyle>;
  /** Columna final del merge (misma fila salvo rowSpan). */
  readonly colEnd?: string;
  /** Filas EXTRA que abarca el merge (0 = solo su fila). */
  readonly rowSpan?: number;
}

/**
 * Sección apilable de un formulario. `rowCount` incluye las filas
 * vacías de respiro, así el bloque siguiente arranca donde debe.
 */
export interface FormBlock {
  readonly rowCount: number;
  readonly cells: ReadonlyArray<FormBlockCell>;
  /** Altos por fila relativa (0-based). */
  readonly rowHeights?: Readonly<Record<number, number>>;
}

export interface StackedForm {
  readonly cells: ReadonlyArray<FormCell>;
  readonly rowHeights: Readonly<Record<number, number>>;
  /** Próxima fila libre después del último bloque. */
  readonly nextRow: number;
}

/**
 * Apila bloques verticalmente a partir de `startRow`. Los bloques
 * null/undefined se omiten y el resto se recorre hacia arriba: éste
 * es el punto de extensión para secciones condicionales de un
 * formato (variantes que agregan o quitan secciones).
 */
export function stackFormBlocks(
  startRow: number,
  blocks: ReadonlyArray<FormBlock | null | undefined>,
): StackedForm {
  const cells: FormCell[] = [];
  const rowHeights: Record<number, number> = {};
  let row = startRow;

  for (const block of blocks) {
    if (!block) continue;
    for (const cell of block.cells) {
      cells.push(toFormCell(cell, row));
    }
    for (const [rel, height] of Object.entries(
      block.rowHeights ?? {},
    )) {
      rowHeights[row + Number(rel)] = height;
    }
    row += block.rowCount;
  }

  return { cells, rowHeights, nextRow: row };
}

function toFormCell(cell: FormBlockCell, baseRow: number): FormCell {
  const row = baseRow + cell.row;
  const ref = `${cell.col}${row}`;
  const spansCols = cell.colEnd !== undefined;
  const spansRows = (cell.rowSpan ?? 0) > 0;
  const merge =
    spansCols || spansRows
      ? `${ref}:${cell.colEnd ?? cell.col}${row + (cell.rowSpan ?? 0)}`
      : undefined;

  return {
    ref,
    merge,
    value: cell.value,
    formula: cell.formula,
    dataValidation: cell.dataValidation,
    numFmt: cell.numFmt,
    style: cell.style,
  };
}
